import React from 'react';
import { Box, Button, IconButton, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import ZoomableImage from '../../components/shared/ZoomableImage';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm6_u7_x2_rcd';
const IMAGE_BASE = '/assets/drawable/';
const IMAGE_KEYS = ['img_0'];
const IMAGE_KEYS_DYNAMIC = ['img_0'];

interface RcdInfo {
  name: string;
  titel: string;
  allgemeineBeschreibung: string;
  auslegungsHinweise: string;
  gesetzeNormenRelevanz: string;
  details: string;
}

const rcdTypen: RcdInfo[] = [
  {
    name: 'RCD Typ A (Standard)',
    titel: 'Fehlerstrom-Schutzeinrichtung (RCD) Typ A',
    allgemeineBeschreibung: 'Ein RCD (Residual Current Device), auch FI-Schutzschalter genannt, ist eine Schutzeinrichtung, die vor gefährlichen Fehlerströmen gegen Erde schützt. Er dient primär dem Personenschutz (Verhinderung lebensgefährlicher Stromschläge) und dem Brandschutz (Verhinderung von Bränden durch Fehlerströme).',
    auslegungsHinweise: 'Die Auslegung eines RCDs erfolgt nach dem Bemessungsfehlerstrom (z.B. 30mA für Personenschutz) und dem Nennstrom (Belastbarkeit). Typ A RCDs erfassen Wechselfehlerströme und pulsierende Gleichfehlerströme. Sie sind der Standard in vielen Hausinstallationen.',
    gesetzeNormenRelevanz: 'In Deutschland fordert die DIN VDE 0100-410 den Einsatz von RCDs mit einem Bemessungsdifferenzstrom von maximal 30 mA für Steckdosenstromkreise (bis 32 A) im Laienbereich und für Endstromkreise im Außenbereich. Weitere spezifische Anforderungen können je nach Anwendungsbereich (z.B. Baustellen, medizinische Bereiche) bestehen.',
    details: '**Typ A RCD (Standard):**\n- **Erfasste Fehlerströme:** Sinusförmige Wechselfehlerströme und pulsierende Gleichfehlerströme.\n- **Bemessungsfehlerstrom (IΔn):** Typisch 10mA (erhöhter Personenschutz), 30mA (Personenschutz), 100mA, 300mA, 500mA (Anlagenschutz, Brandschutz).\n- **Nennstrom (In):** Gibt die maximale Dauerstrombelastbarkeit des RCDs an (z.B. 25A, 40A, 63A). Der Nennstrom des RCD muss mindestens so groß sein wie der Nennstrom der vorgeschalteten Sicherung oder der erwartete Betriebsstrom.\n- **Auslösezeit:** Muss innerhalb definierter Grenzen liegen (z.B. < 0,4s bei 1x IΔn, < 0,15s bei 2x IΔn, < 0,04s bei 5x IΔn für nicht verzögerte Typen).\n- **Anwendungsbereiche:** Standard-Hausinstallationen, Steckdosen, Beleuchtung, wo keine glatten Gleichfehlerströme von Verbrauchern erwartet werden (z.B. durch Frequenzumrichter ohne Filter, bestimmte Ladegeräte).\n- **Wichtig:** RCDs müssen regelmäßig (z.B. halbjährlich) über die Prüftaste auf ihre Funktion überprüft werden!'
  },
  {
    name: 'RCD Typ F (Mischfrequenzen)',
    titel: 'Fehlerstrom-Schutzeinrichtung (RCD) Typ F',
    allgemeineBeschreibung: 'RCDs vom Typ F bieten zusätzlich zur Erfassung von Typ A Fehlerströmen auch Schutz bei Fehlerströmen mit Mischfrequenzen, wie sie bei modernen einphasigen Geräten mit Frequenzumrichtern (z.B. Waschmaschinen, Wärmepumpen) auftreten können.',
    auslegungsHinweise: 'Typ F wird eingesetzt, wenn Verbraucher vorhanden sind, die Fehlerströme mit Frequenzanteilen erzeugen, die von einem Typ A RCD nicht sicher erkannt werden könnten. Dies erhöht die Sicherheit und vermeidet Fehlauslösungen oder Nichtauslösungen.',
    gesetzeNormenRelevanz: 'Der Einsatz von Typ F RCDs wird empfohlen oder gefordert, wenn Gerätehersteller dies in ihren Anschlussbedingungen vorschreiben (z.B. für bestimmte Wärmepumpen, Klimageräte, Waschmaschinen mit Frequenzumrichter).',
    details: '**Typ F RCD (Mischfrequenzen):**\n- **Erfasste Fehlerströme:** Wie Typ A (sinusförmige Wechsel- und pulsierende Gleichfehlerströme) PLUS Fehlerströme mit Mischfrequenzen bis zu 1 kHz.\n- **Stoßstromfestigkeit:** Erhöhte Stoßstromfestigkeit (typ. > 3 kA) gegen ungewolltes Auslösen durch betriebsbedingte Ableitströme.\n- **Anwendungsbereiche:** Haushaltsgeräte mit einphasigen Frequenzumrichtern (z.B. moderne Waschmaschinen, Trockner, Heizungs-/Klimageräte), Beleuchtungsanlagen mit elektronischen Vorschaltgeräten, die Mischfrequenz-Fehlerströme erzeugen können.\n- **Vorteile:** Sicherere Erfassung von Fehlerströmen moderner Geräte, höhere Ausfallsicherheit.'
  },
  {
    name: 'RCD Typ B (Allstromsensitiv)',
    titel: 'Fehlerstrom-Schutzeinrichtung (RCD) Typ B',
    allgemeineBeschreibung: 'Allstromsensitive RCDs vom Typ B erfassen zusätzlich zu den Fehlerstromformen von Typ A und F auch glatte Gleichfehlerströme. Diese können z.B. bei dreiphasigen Frequenzumrichtern, Photovoltaikanlagen oder medizinischen Geräten auftreten.',
    auslegungsHinweise: 'Typ B RCDs sind notwendig, wenn Verbraucher eingesetzt werden, die glatte Gleichfehlerströme erzeugen können. Ein vorgeschalteter Typ A oder F RCD könnte durch solche Gleichfehlerströme \'blind\' werden und im Fehlerfall nicht mehr auslösen.',
    gesetzeNormenRelevanz: 'Der Einsatz von Typ B RCDs ist zwingend erforderlich, wenn die Möglichkeit besteht, dass glatte Gleichfehlerströme > 6mA auftreten. Dies ist häufig bei Anlagen mit Frequenzumrichtern (z.B. Aufzüge, Rolltreppen, Werkzeugmaschinen), USV-Anlagen, Photovoltaik-Wechselrichtern (je nach Topologie) und in medizinischen Bereichen der Fall. Die DIN VDE 0100-530 gibt hierzu detaillierte Vorgaben.',
    details: '**Typ B RCD (Allstromsensitiv):**\n- **Erfasste Fehlerströme:** Wie Typ F (sinusförmige Wechsel-, pulsierende Gleichfehlerströme, Mischfrequenzen) PLUS glatte Gleichfehlerströme.\n- **Frequenzbereich:** Erfassen Fehlerströme über einen weiten Frequenzbereich (z.B. bis 2 kHz oder höher).\n- **Anwendungsbereiche:**\n  • Dreiphasige Frequenzumrichter\n  • Photovoltaikanlagen (abhängig von Wechselrichtertyp und Netzanschluss)\n  • Ladestationen für Elektrofahrzeuge (oft Typ B oder Typ A-EV gefordert)\n  • Medizinische Geräte und Bereiche (z.B. Gruppe 2 Räume nach VDE 0100-710)\n  • USV-Anlagen\n  • Baustromverteiler mit Frequenzumrichtern\n- **Wichtig:** Es gibt auch Typ B+ RCDs, die einen erweiterten Frequenzbereich (z.B. bis 20 kHz) für den Brandschutz abdecken.\n- **Auswahl:** Die genaue Auswahl (z.B. Typ B, B+) hängt von der Analyse der möglichen Fehlerströme der angeschlossenen Geräte ab.'
  },
  {
    name: 'RCD Typ A-EV (E-Mobilität)',
    titel: 'Fehlerstrom-Schutzeinrichtung (RCD) Typ A-EV',
    allgemeineBeschreibung: 'RCDs vom Typ A-EV sind speziell für den Schutz von Ladepunkten für Elektrofahrzeuge konzipiert. Sie erkennen Wechselfehlerströme, pulsierende Gleichfehlerströme und zusätzlich glatte Gleichfehlerströme bis 6mA.',
    auslegungsHinweise: 'Für die Errichtung von Ladepunkten für Elektrofahrzeuge ist oft ein RCD Typ A-EV oder ein RCD Typ B vorgeschrieben, um eine sichere Abschaltung bei Gleichfehlerströmen > 6mA, die vom Fahrzeugladegerät verursacht werden könnten, zu gewährleisten.',
    gesetzeNormenRelevanz: 'Die Normen für die Errichtung von Ladepunkten (z.B. DIN VDE 0100-722) fordern geeignete Maßnahmen zum Schutz gegen Gleichfehlerströme. Ein RCD Typ A allein ist hier oft nicht ausreichend.',
    details: '**Typ A-EV RCD (Elektromobilität):**\n- **Erfasste Fehlerströme:** Wie Typ A, aber mit zusätzlicher Erkennung von glatten Gleichfehlerströmen bis zu 6mA.\n- **Funktion:** Wenn das Elektrofahrzeug einen glatten Gleichfehlerstrom > 6mA verursacht, muss der RCD Typ A-EV sicher auslösen.\n- **Alternative:** Statt eines Typ A-EV kann oft auch ein Typ B RCD verwendet werden, da dieser glatte Gleichfehlerströme generell erfasst.\n- **Anwendungsbereiche:** Speziell für Ladestationen (Wallboxen, Ladesäulen) für Elektrofahrzeuge.\n- **Wichtigkeit:** Verhindert, dass ein vorgeschalteter Typ A RCD durch den Gleichfehlerstrom des Fahrzeugs \'erblindet\' und seine Schutzfunktion verliert.'
  }
];

const introBlocks = [
  {
    heading: 'Fehlerstrom-Schutzeinrichtungen (RCD / FI)',
    body: 'RCDs (Residual Current Devices), in Deutschland auch FI-Schutzschalter (Fehlerstrom-Schutzschalter) genannt, sind Schutzeinrichtungen in elektrischen Anlagen, die vor gefährlichen Fehlerströmen gegen Erde schützen. Ihre Hauptaufgaben sind der Personenschutz (Schutz vor elektrischem Schlag) und der Brandschutz (Verhinderung von Bränden durch Fehlerströme).'
  },
  {
    heading: 'Funktionsprinzip',
    body: 'Ein RCD überwacht kontinuierlich die Summe der Ströme, die über die Hinleiter (z.B. L1, L2, L3) zur Last fließen und über die Rückleiter (N) zurückfließen. In einem fehlerfreien Stromkreis ist diese Summe null. Tritt ein Fehlerstrom auf (z.B. durch eine beschädigte Isolation oder einen direkten Kontakt einer Person mit einem spannungsführenden Teil), fließt ein Teil des Stroms über die Erde zurück und die Summe ist nicht mehr null. Der RCD erkennt diese Differenz und löst bei Überschreiten des Bemessungsfehlerstroms (z.B. 30mA) innerhalb kurzer Zeit aus und trennt den Stromkreis.'
  }
];

const M6_U7_X2_RCD: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM6, canUploadImagesM6 } = useAuth();
  const canEditTexts = canEditTextsM6;
  const canUploadImages = canUploadImagesM6;
  const { override, refresh } = useUnitOverride(PAGE_ID);

  const getImageSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    let s = String(v).trim();
    if (s.includes('/')) {
      s = s.split('/').pop() || s;
    }
    return `/assets/unit_custom/${s}`;
  };

  const getDynamicImageSrc = (selectedType: string, idx: number, fallback: string) => {
    const key = `dyn_${selectedType}_img_${idx}`;
    return getImageSrc(key, fallback);
  };

  const [selectedType, setSelectedType] = React.useState<string>(rcdTypen[0].name);
  const currentInfo = rcdTypen.find(r => r.name === selectedType) || rcdTypen[0];

  const mergedIntroBlocks = React.useMemo(
    () => introBlocks.map((tb, i) => ({
      ...tb,
      body: override?.texts?.[`intro_${i}`] || tb.body
    })),
    [override]
  );

  const mergedDynamicBlocks = React.useMemo(() => [
    {
      heading: currentInfo.titel,
      body: override?.texts?.[`${selectedType}_0`] || currentInfo.allgemeineBeschreibung
    },
    {
      heading: 'Hinweise zur Auslegung',
      body: override?.texts?.[`${selectedType}_1`] || currentInfo.auslegungsHinweise
    },
    {
      heading: 'Relevante Gesetze und Normen',
      body: override?.texts?.[`${selectedType}_2`] || currentInfo.gesetzeNormenRelevanz
    },
    {
      heading: 'Detaillierte Informationen',
      body: override?.texts?.[`${selectedType}_3`] || currentInfo.details
    }
  ], [selectedType, override, currentInfo]);

  const allTextBlocks = React.useMemo(
    () => [...mergedIntroBlocks, ...mergedDynamicBlocks],
    [mergedIntroBlocks, mergedDynamicBlocks]
  );

  const fullPlainText = React.useMemo(() => 
    allTextBlocks.map(b => `${b.heading}. ${b.body}`).join(' '),
    [allTextBlocks]
  );

  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  const handleImageReplace = React.useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      await refresh();
    } catch (error) {
      console.error('Fehler beim Bild-Upload:', error);
      alert('Fehler beim Hochladen des Bildes.');
    }
  }, [token, refresh]);

  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token);
      await refresh();
    } catch (error) {
      console.error('Fehler beim Text-Speichern:', error);
      alert('Fehler beim Speichern des Textes.');
    }
  }, [token, refresh]);

  const handleTypeChange = (event: any) => {
    setSelectedType(String(event.target.value));
  };

  const ttsButton = React.createElement(IconButton, {
    onClick: speaking ? stop : speak,
    'aria-label': 'Text vorlesen',
    sx: { ml: 1 }
  }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '6.7.2 RCD / FI-Schutzschalter', actions: ttsButton },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        IMAGE_KEYS.map((key) =>
          React.createElement(ZoomableImage, {
            key: key,
            src: getImageSrc(key, `${IMAGE_BASE}zzz_help.png`),
            alt: 'RCD',
            label: 'RCD-Bild',
            onReplace: canUploadImages ? (file: File) => handleImageReplace(key, file) : undefined
          })
        )
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        React.createElement(EditableTextBlock, {
          key: 0,
          pageId: PAGE_ID,
          blockId: 'intro_0',
          heading: mergedIntroBlocks[0].heading,
          body: mergedIntroBlocks[0].body,
          onSave: (newBody: string) => handleTextSave('intro_0', newBody)
        }),
        React.createElement(EditableTextBlock, {
          key: 1,
          pageId: PAGE_ID,
          blockId: 'intro_1',
          heading: mergedIntroBlocks[1].heading,
          body: mergedIntroBlocks[1].body,
          onSave: (newBody: string) => handleTextSave('intro_1', newBody)
        }),
        React.createElement(FormControl, { fullWidth: true, sx: { mt: 2, mb: 2 } },
          React.createElement(InputLabel, { id: 'rcd-select-label' }, 'RCD-Typ auswählen'),
          React.createElement(Select, {
            labelId: 'rcd-select-label',
            id: 'rcd-select',
            value: selectedType,
            label: 'RCD-Typ auswählen',
            onChange: handleTypeChange
          },
            ...rcdTypen.map((typ) =>
              React.createElement(MenuItem, { key: typ.name, value: typ.name }, typ.name)
            )
          )
        ),
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
          IMAGE_KEYS_DYNAMIC.map((key) =>
            React.createElement(ZoomableImage, {
              key: key,
              src: getDynamicImageSrc(selectedType, 0, `${IMAGE_BASE}zzz_help.png`),
              alt: 'Bild für Spinner-Typ',
              label: 'Spinner-Bild',
              onReplace: canUploadImages ? (file: File) => handleImageReplace(`dyn_${selectedType}_img_0`, file) : undefined
            })
          )
        ),
        React.createElement(EditableTextBlock, {
          key: `${selectedType}_0`,
          pageId: PAGE_ID,
          blockId: `dyn_${selectedType}_0`,
          heading: currentInfo.titel,
          body: override?.texts?.[`dyn_${selectedType}_0`] || currentInfo.allgemeineBeschreibung,
          onSave: (newBody: string) => handleTextSave(`dyn_${selectedType}_0`, newBody)
        }),
        React.createElement(EditableTextBlock, {
          key: `${selectedType}_1`,
          pageId: PAGE_ID,
          blockId: `dyn_${selectedType}_1`,
          heading: 'Hinweise zur Auslegung',
          body: override?.texts?.[`dyn_${selectedType}_1`] || currentInfo.auslegungsHinweise,
          onSave: (newBody: string) => handleTextSave(`dyn_${selectedType}_1`, newBody)
        }),
        React.createElement(EditableTextBlock, {
          key: `${selectedType}_2`,
          pageId: PAGE_ID,
          blockId: `dyn_${selectedType}_2`,
          heading: 'Relevante Gesetze und Normen',
          body: override?.texts?.[`dyn_${selectedType}_2`] || currentInfo.gesetzeNormenRelevanz,
          onSave: (newBody: string) => handleTextSave(`dyn_${selectedType}_2`, newBody)
        }),
        React.createElement(EditableTextBlock, {
          key: `${selectedType}_3`,
          pageId: PAGE_ID,
          blockId: `dyn_${selectedType}_3`,
          heading: 'Detaillierte Informationen',
          body: override?.texts?.[`dyn_${selectedType}_3`] || currentInfo.details,
          onSave: (newBody: string) => handleTextSave(`dyn_${selectedType}_3`, newBody)
        }),
        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
          React.createElement(Button, {
            variant: 'outlined',
            onClick: () => navigate('/units/m6_u7_x99_quiz')
          }, 'Zum Quiz')
        )
      )
    )
  );
};

export default M6_U7_X2_RCD;


